/**
 * Slider Component JavaScript
 * Arco Design Slider Implementation
 */

class SliderComponent {
  constructor(element) {
    this.el = element;
    this.track = element.querySelector('.slider__track');
    this.fill = element.querySelector('.slider__fill');
    this.handles = element.querySelectorAll('.slider__handle');
    this.marks = element.querySelectorAll('.slider__mark');
    
    this.min = parseFloat(element.dataset.sliderMin) || 0;
    this.max = parseFloat(element.dataset.sliderMax) || 100;
    this.step = parseFloat(element.dataset.sliderStep) || 1;
    this.isRange = element.dataset.sliderRange === 'true';
    this.isDisabled = element.hasAttribute('data-disabled');
    
    // Current values
    this.values = {
      min: this.min,
      max: this.max,
      single: this.min
    };
    
    // Active handle being dragged
    this.activeHandle = null;
    
    this.init();
  }
  
  init() {
    if (this.isDisabled) return;
    
    // Initialize values from DOM
    this.initializeValues();
    
    // Bind events
    this.handles.forEach(handle => {
      handle.addEventListener('mousedown', (e) => this.handleMouseDown(e, handle));
      handle.addEventListener('touchstart', (e) => this.handleTouchStart(e, handle), { passive: false });
      handle.addEventListener('keydown', (e) => this.handleKeydown(e, handle));
    });
    
    // Track click
    this.track.addEventListener('click', (e) => this.handleTrackClick(e));
    
    // Mark click
    this.marks.forEach(mark => {
      mark.addEventListener('click', (e) => this.handleMarkClick(e, mark));
    });
    
    // Input fields
    this.el.querySelectorAll('.slider__input').forEach(input => {
      input.addEventListener('change', (e) => this.handleInputChange(e, input));
    });
    
    // Global mouse/touch events
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    document.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    document.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    document.addEventListener('touchend', (e) => this.handleTouchEnd(e));
  }
  
  initializeValues() {
    this.handles.forEach(handle => {
      const handleType = handle.dataset.handle;
      const currentValue = parseFloat(handle.getAttribute('aria-valuenow'));
      
      if (handleType === 'min') {
        this.values.min = currentValue;
      } else if (handleType === 'max') {
        this.values.max = currentValue;
      } else {
        this.values.single = currentValue;
      }
    });
  }
  
  handleMouseDown(e, handle) {
    e.preventDefault();
    this.startDrag(handle);
  }
  
  handleTouchStart(e, handle) {
    e.preventDefault();
    this.startDrag(handle);
  }
  
  startDrag(handle) {
    this.activeHandle = handle;
    handle.classList.add('is-dragging');
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }
  
  handleMouseMove(e) {
    if (!this.activeHandle) return;
    this.updateFromPosition(e.clientX);
  }
  
  handleTouchMove(e) {
    if (!this.activeHandle) return;
    e.preventDefault();
    const touch = e.touches[0];
    this.updateFromPosition(touch.clientX);
  }
  
  handleMouseUp() {
    this.endDrag();
  }
  
  handleTouchEnd() {
    this.endDrag();
  }
  
  endDrag() {
    if (this.activeHandle) {
      this.activeHandle.classList.remove('is-dragging');
      this.activeHandle = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.dispatchChange();
    }
  }
  
  handleTrackClick(e) {
    if (this.isDisabled) return;
    if (e.target.closest('.slider__handle')) return;
    
    const value = this.getValueFromPosition(e.clientX);
    
    if (this.isRange) {
      // Determine which handle is closer
      const distToMin = Math.abs(value - this.values.min);
      const distToMax = Math.abs(value - this.values.max);
      
      if (distToMin <= distToMax) {
        this.setValue('min', value);
      } else {
        this.setValue('max', value);
      }
    } else {
      this.setValue('single', value);
    }
    
    this.dispatchChange();
  }
  
  handleMarkClick(e, mark) {
    if (this.isDisabled) return;
    e.stopPropagation();
    
    const value = parseFloat(mark.dataset.value);
    
    if (this.isRange) {
      // Determine which handle is closer
      const distToMin = Math.abs(value - this.values.min);
      const distToMax = Math.abs(value - this.values.max);
      
      if (distToMin <= distToMax) {
        this.setValue('min', value);
      } else {
        this.setValue('max', value);
      }
    } else {
      this.setValue('single', value);
    }
    
    this.dispatchChange();
  }
  
  handleKeydown(e, handle) {
    const handleType = handle.dataset.handle;
    let currentValue;
    
    if (handleType === 'min') {
      currentValue = this.values.min;
    } else if (handleType === 'max') {
      currentValue = this.values.max;
    } else {
      currentValue = this.values.single;
    }
    
    let newValue = currentValue;
    
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        newValue = Math.max(this.min, currentValue - this.step);
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        newValue = Math.min(this.max, currentValue + this.step);
        break;
      case 'Home':
        e.preventDefault();
        newValue = this.min;
        break;
      case 'End':
        e.preventDefault();
        newValue = this.max;
        break;
      case 'PageDown':
        e.preventDefault();
        newValue = Math.max(this.min, currentValue - this.step * 10);
        break;
      case 'PageUp':
        e.preventDefault();
        newValue = Math.min(this.max, currentValue + this.step * 10);
        break;
      default:
        return;
    }
    
    this.setValue(handleType, newValue);
    this.dispatchChange();
  }
  
  handleInputChange(e, input) {
    let value = parseFloat(input.value);
    value = Math.max(this.min, Math.min(this.max, value));
    value = this.snapToStep(value);
    
    if (input.classList.contains('slider__input--min')) {
      this.setValue('min', value);
    } else if (input.classList.contains('slider__input--max')) {
      this.setValue('max', value);
    } else {
      this.setValue('single', value);
    }
    
    this.dispatchChange();
  }
  
  updateFromPosition(clientX) {
    const value = this.getValueFromPosition(clientX);
    const handleType = this.activeHandle.dataset.handle;
    this.setValue(handleType, value);
  }
  
  getValueFromPosition(clientX) {
    const rect = this.track.getBoundingClientRect();
    let percent = (clientX - rect.left) / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    
    let value = this.min + percent * (this.max - this.min);
    value = this.snapToStep(value);
    
    return value;
  }
  
  snapToStep(value) {
    const steps = Math.round((value - this.min) / this.step);
    return this.min + steps * this.step;
  }
  
  setValue(handleType, value) {
    value = Math.max(this.min, Math.min(this.max, value));
    value = this.snapToStep(value);
    
    // Range constraints
    if (this.isRange) {
      if (handleType === 'min' && value > this.values.max) {
        value = this.values.max;
      } else if (handleType === 'max' && value < this.values.min) {
        value = this.values.min;
      }
    }
    
    // Update internal value
    if (handleType === 'min') {
      this.values.min = value;
    } else if (handleType === 'max') {
      this.values.max = value;
    } else {
      this.values.single = value;
    }
    
    this.updateUI();
  }
  
  updateUI() {
    const range = this.max - this.min;
    
    if (this.isRange) {
      const percentMin = ((this.values.min - this.min) / range) * 100;
      const percentMax = ((this.values.max - this.min) / range) * 100;
      
      // Update fill
      this.fill.style.left = percentMin + '%';
      this.fill.style.width = (percentMax - percentMin) + '%';
      
      // Update handles
      this.handles.forEach(handle => {
        const handleType = handle.dataset.handle;
        if (handleType === 'min') {
          handle.style.left = percentMin + '%';
          handle.setAttribute('aria-valuenow', this.values.min);
          const tooltip = handle.querySelector('.slider__tooltip-text');
          if (tooltip) tooltip.textContent = this.values.min;
        } else if (handleType === 'max') {
          handle.style.left = percentMax + '%';
          handle.setAttribute('aria-valuenow', this.values.max);
          const tooltip = handle.querySelector('.slider__tooltip-text');
          if (tooltip) tooltip.textContent = this.values.max;
        }
      });
      
      // Update inputs
      const minInput = this.el.querySelector('.slider__input--min, .slider__hidden-input--min');
      const maxInput = this.el.querySelector('.slider__input--max, .slider__hidden-input--max');
      if (minInput) minInput.value = this.values.min;
      if (maxInput) maxInput.value = this.values.max;
    } else {
      const percent = ((this.values.single - this.min) / range) * 100;
      
      // Update fill
      this.fill.style.left = '0%';
      this.fill.style.width = percent + '%';
      
      // Update handle
      const handle = this.handles[0];
      if (handle) {
        handle.style.left = percent + '%';
        handle.setAttribute('aria-valuenow', this.values.single);
        const tooltip = handle.querySelector('.slider__tooltip-text');
        if (tooltip) tooltip.textContent = this.values.single;
      }
      
      // Update input
      const input = this.el.querySelector('.slider__input, .slider__hidden-input');
      if (input) input.value = this.values.single;
    }
    
    // Update mark states
    this.updateMarks();
  }
  
  updateMarks() {
    this.marks.forEach(mark => {
      const markValue = parseFloat(mark.dataset.value);
      let isActive = false;
      
      if (this.isRange) {
        isActive = markValue >= this.values.min && markValue <= this.values.max;
      } else {
        isActive = markValue <= this.values.single;
      }
      
      mark.classList.toggle('slider__mark--active', isActive);
    });
  }
  
  dispatchChange() {
    const detail = this.isRange
      ? { min: this.values.min, max: this.values.max }
      : { value: this.values.single };
    
    const event = new CustomEvent('slider:change', {
      bubbles: true,
      detail: {
        name: this.el.dataset.sliderName,
        ...detail
      }
    });
    
    this.el.dispatchEvent(event);
    
    // Also dispatch native input event on hidden inputs
    this.el.querySelectorAll('.slider__hidden-input, .slider__input').forEach(input => {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
  
  // Public API
  getValue() {
    return this.isRange
      ? { min: this.values.min, max: this.values.max }
      : this.values.single;
  }
  
  setRange(min, max) {
    if (this.isRange) {
      this.setValue('min', min);
      this.setValue('max', max);
    }
  }
}

// Auto-initialize all slider components
function initSliderComponents() {
  document.querySelectorAll('[data-slider]').forEach(el => {
    if (!el._sliderComponent) {
      el._sliderComponent = new SliderComponent(el);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSliderComponents);
} else {
  initSliderComponents();
}

// Re-initialize on Shopify section events
document.addEventListener('shopify:section:load', initSliderComponents);

// Export for external use
window.SliderComponent = SliderComponent;

